import React, { useMemo } from "react";

import NorthbeamLogo from "@assets/northbeam-logo.svg";
import { useUser } from "@components/user-context";
import { MiniGraph } from "@features/creative-analytics/components/ca-card";
import { usePageConfiguration } from "@features/sales-v3/hooks";
import { type GetCampaign_me_adObject_topLevel } from "@north-beam/nb-client-side";
import {
  makeFlyoutPerformanceRows,
  preprocessForChart,
  MetricFormatters,
} from "@north-beam/nb-client-side";
import { metrics as metricsPkg } from "@north-beam/nb-common";

import { type Level } from "../../types";

interface Props {
  adObjectLevel: Level;
  topLevel: GetCampaign_me_adObject_topLevel | null;
}

const AdLabel: { [key in Level]: string } = {
  campaign: "Campaign performance",
  adset: "Adset performance",
  ad: "Ad performance",
};

type GenericChartDatum = {
  id?: string;
  name: string;
  formatted: string;
};

type RoasChartDatum = {
  id: "roas";
  name: string;
  formatted: string;
  chartData: {
    data: number[];
    average: number;
    isPositiveChangeGood: boolean;
  };
};

type ChartDatum = GenericChartDatum | RoasChartDatum;

const isRoas = (datum: ChartDatum): datum is RoasChartDatum =>
  datum.id === "roas";

const isValid = (v: number) => !isNaN(v) && isFinite(v);

const sum = (vs: number[]) => vs.reduce((prev, curr) => prev + curr, 0);

export const TopLevel = ({ adObjectLevel, topLevel }: Props) => {
  const { user } = useUser();
  const config = usePageConfiguration();

  const metricsData: ChartDatum[] = useMemo(() => {
    if (!topLevel || topLevel.rows.length === 0) {
      return [];
    }
    const chartableRowsRoot = preprocessForChart(topLevel.rows, [], []);
    if (!chartableRowsRoot) {
      return [];
    }

    const metrics = makeFlyoutPerformanceRows(
      config.attributionModel,
      config.attributionWindow,
      config.accountingMode,
      config.timeGranularity,
      user,
    );

    return metrics.map((metric) => {
      const metricIsRoas = metric.metadata?.metricId === "roas";
      const chartSeries: number[] = [];

      const total = metricsPkg.addMetricsToPoint(function* () {
        for (const date in chartableRowsRoot.series) {
          if (date >= topLevel.startDate) {
            const point = chartableRowsRoot.series[date];
            if (metricIsRoas) {
              const value = metric.calculate(point);
              chartSeries.push(value);
            }
            yield point;
          }
        }
      });

      const valids = chartSeries.filter(isValid);
      const value = metric.calculate(total);

      if (metricIsRoas) {
        return {
          id: "roas",
          name: metric.name,
          formatted: MetricFormatters.decimal(
            value,
            user.currencies.currentCurrency,
            {
              precision: "approximate",
            },
          ),
          chartData: {
            data: chartSeries,
            average: valids.length !== 0 ? sum(chartSeries) / valids.length : 0,
            isPositiveChangeGood: metric.isPositiveChangeGood,
          },
        };
      }
      return {
        name: metric.name,
        id: metric.spec?.id,
        formatted: metric.formatter(value, user.currencies.currentCurrency, {
          precision: "approximate",
        }),
      };
    });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [topLevel, config]);

  if (metricsData.length === 0) {
    return (
      <div className="text-center my-32">
        No performance data for this Traffic Source.
      </div>
    );
  }
  return (
    <div className="mt-8">
      <h3 className="text-lg font-bold">{AdLabel[adObjectLevel]}</h3>
      <div className="flex flex-wrap gap-3">
        {metricsData.map((e) =>
          isRoas(e) ? (
            <TileRoas
              key={e.id}
              name={e.name}
              value={e.formatted}
              chartData={e.chartData}
            />
          ) : (
            <TileSimple key={e.id} name={e.name} value={e.formatted} />
          ),
        )}
      </div>
    </div>
  );
};

const TileSimple = ({ name, value }: { name: string; value: string }) => (
  <div className="w-[calc(50%-6px)] border rounded p-3">
    <h4 className="text-xs font-bold mb-3">{name}</h4>
    <div className="text-4xl text-cosmic font-light">{value}</div>
  </div>
);

const TileRoas = ({
  name,
  value,
  chartData,
}: {
  name: string;
  value: string;
  chartData: {
    data: number[];
    average: number;
    isPositiveChangeGood: boolean;
  } | null;
}) => (
  <div className="w-[calc(50%-6px)] border rounded p-3">
    <h4 className="text-xs font-bold mb-3">{name}</h4>
    <div className="flex-1 flex items-center justify-between">
      <div className="shrink-0 bg-northbeam-icon rounded-full p-[3px] w-8 h-8 mr-2">
        <img
          className="w-full h-full"
          alt="northbeam-logo"
          src={NorthbeamLogo}
        />
      </div>
      <div className="flex-1 text-4xl text-cosmic font-light">{value}x</div>
      {chartData && (
        <div className="h-8 w-16 mx-2">
          <MiniGraph
            data={chartData.data}
            average={chartData.average}
            isPositiveChangeGood={chartData.isPositiveChangeGood}
          />
        </div>
      )}
    </div>
  </div>
);
